import { AfterViewInit, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { CP_ICONS } from '../shared/icons';
import { scrollToSectionWhenReady } from '../shared/navigation';
import { PageShellV3Component } from '../shared/page-shell.component';
import { HeroV3Component } from './hero/hero-v3.component';
import { ProcessV3Component } from './process/process-v3.component';
import { SolutionsV3Component } from './solutions/solutions-v3.component';
import { PricingV3Component } from './pricing/pricing-v3.component';
import { CtaV3Component } from './cta/cta-v3.component';
import { FaqV3Component } from './faq/faq-v3.component';

const TOAST_MS = 5000;
const MOVED_SECTIONS = new Set(['about', 'contact']);

/**
 * v3 home page. Renders its own navbar/footer (route data `hideShell: true`).
 * Keeps v2 behaviour: `?message=` from the subscription guard shows a toast and
 * jumps to pricing. Any `#section` deep link scrolls on load.
 */
@Component({
  selector: 'app-home-v3',
  standalone: true,
  imports: [
    NgIcon,
    PageShellV3Component,
    HeroV3Component,
    ProcessV3Component,
    SolutionsV3Component,
    PricingV3Component,
    CtaV3Component,
    FaqV3Component,
  ],
  viewProviders: [provideIcons(CP_ICONS)],
  templateUrl: './home-v3.component.html',
})
export class HomeV3Component implements AfterViewInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private toastTimer?: ReturnType<typeof setTimeout>;

  readonly subscriptionMessage = signal<string | null>(null);

  constructor() {
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      const message = params['message'];
      if (typeof message !== 'string' || !message) {
        return;
      }
      this.showToast(message);
      this.router.navigate([], { relativeTo: this.route, queryParams: {}, fragment: 'pricing', replaceUrl: true, scroll: 'manual' });
    });

    this.route.fragment.pipe(takeUntilDestroyed()).subscribe((fragment) => {
      if (fragment && MOVED_SECTIONS.has(fragment)) {
        // About and Contact moved to their own pages; keep old /#about and /#contact links working.
        this.router.navigate(['/' + fragment], { replaceUrl: true });
      } else if (fragment) {
        scrollToSectionWhenReady(fragment);
      }
    });

    this.destroyRef.onDestroy(() => clearTimeout(this.toastTimer));
  }

  ngAfterViewInit(): void {
    const fragment = this.route.snapshot.fragment;
    if (fragment && !MOVED_SECTIONS.has(fragment)) {
      scrollToSectionWhenReady(fragment);
    }
  }

  dismissToast(): void {
    clearTimeout(this.toastTimer);
    this.subscriptionMessage.set(null);
  }

  private showToast(message: string): void {
    clearTimeout(this.toastTimer);
    this.subscriptionMessage.set(message);
    this.toastTimer = setTimeout(() => this.subscriptionMessage.set(null), TOAST_MS);
  }
}
